/**
 * Favicon Generator Component
 * Generate complete favicon packages from any image
 */

import { memo, useState, useCallback } from 'react';
import { useToast } from '../../context';
import {
  generateFavicons,
  createFaviconPackage,
  generateHtmlSnippet,
  type GeneratedFavicon,
} from '../../services/faviconService';
import { analytics } from '../../services/analyticsService';

// Icons
const UploadIcon = memo(() => (
  <svg className="w-12 h-12 mx-auto mb-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
));
UploadIcon.displayName = 'UploadIcon';

const DownloadIcon = memo(() => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
));
DownloadIcon.displayName = 'DownloadIcon';

const CopyIcon = memo(() => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
));
CopyIcon.displayName = 'CopyIcon';

const SpinnerIcon = memo(() => (
  <svg className="w-8 h-8 animate-spin text-indigo-400" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
));
SpinnerIcon.displayName = 'SpinnerIcon';

const CheckIcon = memo(() => (
  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
));
CheckIcon.displayName = 'CheckIcon';

export const FaviconGenerator = memo(function FaviconGenerator() {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [favicons, setFavicons] = useState<GeneratedFavicon[]>([]);
  const [appName, setAppName] = useState('My App');
  const { success, error, info } = useToast();

  const processFile = useCallback(async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      error('Please upload an image file (PNG, JPG, SVG, etc.)');
      return;
    }

    setIsProcessing(true);
    info('Generating favicons...');

    try {
      // Store original image for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Generate all favicon sizes
      const generatedFavicons = await generateFavicons(file);
      setFavicons(generatedFavicons);
      success('Favicons generated successfully!');
      analytics.favicon.generate(file.type);
    } catch (err) {
      error('Failed to generate favicons');
      console.error('Generation error:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [success, error, info]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDownloadPackage = useCallback(async () => {
    if (favicons.length === 0) return;

    try {
      info('Creating favicon package...');
      const zipBlob = await createFaviconPackage(favicons, appName);
      
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'favicon-package.zip';
      link.click();
      URL.revokeObjectURL(url);
      
      success('Favicon package downloaded!');
      analytics.favicon.downloadPackage();
    } catch (err) {
      error('Failed to create package');
      console.error(err);
    }
  }, [favicons, appName, success, error, info]);

  const handleCopyHtml = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(generateHtmlSnippet());
      success('HTML snippet copied to clipboard!');
    } catch {
      error('Failed to copy to clipboard');
    }
  }, [success, error]);

  const handleClear = useCallback(() => {
    setOriginalImage(null);
    setFavicons([]);
  }, []);

  const handleDownloadSingle = useCallback((favicon: GeneratedFavicon) => {
    const url = URL.createObjectURL(favicon.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = favicon.name;
    link.click();
    URL.revokeObjectURL(url);
    analytics.favicon.downloadSingle(favicon.size);
  }, []);

  return (
    <div className="space-y-6">
      {/* App Name Input */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
        <label className="block text-sm font-medium text-slate-300 mb-2">
          App Name (for manifest)
        </label>
        <input
          type="text"
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          placeholder="My App"
          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:border-indigo-500 focus:outline-none"
        />
      </div>

      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer
          ${isDragging 
            ? 'border-indigo-500 bg-indigo-500/10' 
            : 'border-slate-600 hover:border-indigo-400 bg-slate-800/50'
          }
          ${isProcessing ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isProcessing}
        />
        
        {isProcessing ? (
          <div className="py-4">
            <SpinnerIcon />
            <p className="mt-4 text-slate-400">Generating favicons...</p>
          </div>
        ) : (
          <>
            <UploadIcon />
            <p className="text-slate-300 font-medium">
              Drop your logo/icon here
            </p>
            <p className="text-slate-500 text-sm mt-1">
              PNG, JPG, SVG or any image (square works best)
            </p>
          </>
        )}
      </div>

      {/* Results */}
      {favicons.length > 0 && (
        <>
          {/* Preview Grid */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-300">Generated Favicons</h3>
              <button
                onClick={handleDownloadPackage}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors font-medium"
              >
                <DownloadIcon />
                Download All (ZIP)
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {favicons.map((favicon) => (
                <div
                  key={favicon.name}
                  className="bg-slate-700/50 rounded-lg p-3 text-center group cursor-pointer hover:bg-slate-700 transition-colors"
                  onClick={() => handleDownloadSingle(favicon)}
                >
                  <div className="bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMzMzIi8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiMzMzMiLz48L3N2Zz4=')] rounded flex items-center justify-center p-2 mb-2 min-h-[60px]">
                    <img
                      src={favicon.dataUrl}
                      alt={favicon.name}
                      className="max-w-full max-h-12 object-contain"
                      style={{ imageRendering: favicon.size <= 32 ? 'pixelated' : 'auto' }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 truncate group-hover:text-white transition-colors">
                    {favicon.size}×{favicon.size}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* HTML Snippet */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-300">HTML Code</h3>
              <button
                onClick={handleCopyHtml}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
              >
                <CopyIcon />
                Copy
              </button>
            </div>
            <pre className="bg-slate-900 rounded-lg p-4 overflow-x-auto text-xs text-slate-300 font-mono">
              {generateHtmlSnippet()}
            </pre>
          </div>

          {/* Package Contents */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
            <h3 className="text-sm font-medium text-slate-300 mb-3">📦 Package Contents</h3>
            <div className="grid md:grid-cols-2 gap-2 text-sm">
              {[
                { name: 'favicon.ico', desc: '16, 32, 48px combined' },
                { name: 'favicon-16x16.png', desc: 'Standard favicon' },
                { name: 'favicon-32x32.png', desc: 'Retina favicon' },
                { name: 'apple-touch-icon.png', desc: 'iOS home screen' },
                { name: 'android-chrome-192x192.png', desc: 'Android small' },
                { name: 'android-chrome-512x512.png', desc: 'Android large' },
                { name: 'mstile-150x150.png', desc: 'Windows tiles' },
                { name: 'site.webmanifest', desc: 'PWA manifest' },
                { name: 'browserconfig.xml', desc: 'Windows config' },
              ].map((item) => (
                <div key={item.name} className="flex items-center gap-2 text-slate-400">
                  <CheckIcon />
                  <span className="text-white">{item.name}</span>
                  <span className="text-slate-500">- {item.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Clear Button */}
          <div className="text-center">
            <button
              onClick={handleClear}
              className="px-6 py-2.5 text-sm font-medium bg-slate-700 hover:bg-slate-600 text-white rounded-lg border border-slate-600 hover:border-slate-500 transition-all flex items-center gap-2 mx-auto"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Generate from Another Image
            </button>
          </div>
        </>
      )}

      {/* Info */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <h4 className="text-sm font-medium text-slate-300 mb-2">💡 Tips for best results:</h4>
        <ul className="text-sm text-slate-400 space-y-1 list-disc list-inside">
          <li>Use a square image for best results (will be center-cropped if not)</li>
          <li>Higher resolution source images produce better quality favicons</li>
          <li>Simple icons/logos work better than complex photos</li>
          <li>PNG with transparency is recommended for modern browsers</li>
        </ul>
      </div>
    </div>
  );
});

export default FaviconGenerator;
